import type {
    IWasteMovementDetail,
    WasteMovementBrokerDealerPayload,
    WasteMovementDirection,
} from "@/customTypings/wasteMovement/WasteMovement";
import type { WasteMovementBrokerDealerFormValues, WasteMovementFormValues } from "../model/formValues";

/**
 * S1D broker/dealer capture for Waste Received. The rules mirror the backend (`WasteMovementPartyFormats`,
 * `WasteMovementValidationService.ValidateBrokerDealer`), which follow DEFRA's `brokerOrDealer` validation (Receipt API v1.0
 * and DEFRA's validation package, checked 24 Sep 2026): the organisation name is required; registration number, address,
 * postcode, email and phone are optional; a value that is given must be in a format DEFRA accepts, and an address needs
 * its postcode.
 */

export const BROKER_DEALER_NAME_MAX_LENGTH = 255;
export const BROKER_DEALER_REGISTRATION_MAX_LENGTH = 64;
export const BROKER_DEALER_ADDRESS_MAX_LENGTH = 500;
export const BROKER_DEALER_POSTCODE_MAX_LENGTH = 16;
export const BROKER_DEALER_EMAIL_MAX_LENGTH = 254;
export const BROKER_DEALER_PHONE_MAX_LENGTH = 64;

// DEFRA's registration formats (shared by carriers and brokers/dealers), case-insensitive: England and Wales, SEPA and NIEA.
const REGISTRATION_PATTERNS = [
    /^CBD[LU]\d{3,}$/i,
    /^((WCR\/R\/\d{7})|((SCO|SEA|SNO|SWE|WCR)\/\d{6}))$/i,
    /^PCT-[A-Z]-\d{3,7}$/i,
    /^ROC\W*[UL]T\W*\d{1,5}$/i,
];

// UK postcode (GOV.UK pattern DEFRA uses: one space before the inward code) and Irish Eircode.
const UK_POSTCODE =
    /^((GIR 0A{2})|((([A-Z]\d{1,2})|(([A-Z][A-HJ-Y]\d{1,2})|(([A-Z]\d[A-Z])|([A-Z][A-HJ-Y]\d?[A-Z])))) \d[A-Z]{2}))$/i;
const IRISH_EIRCODE = /^(?:D6W|[AC-FHKNPRTV-Y]\d{2}) ?[0-9AC-FHKNPRTV-Y]{4}$/i;

// Email: the structure DEFRA's email rule checks (see the backend for the full description). DEFRA also checks the
// top-level domain against the IANA list, which is not repeated here.
const EMAIL_LOCAL_SEGMENT = /^[A-Za-z0-9_!#$%&'*+\-/=?^`{|}~\u0080-￿]+$/;
const EMAIL_DOMAIN_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;
const EMAIL_TOP_LEVEL_LABEL = /^[A-Za-z](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;
const EMAIL_DOMAIN_SEPARATORS = "@:/\\#!$&'()*+,;=?%";

export const BROKER_DEALER_REGISTRATION_FORMAT_MESSAGE =
    "Broker or dealer registration number is not in a recognised format, for example CBDU123456 (England and Wales), WCR/R/1234567 (Scotland) or ROC UT 12345 (Northern Ireland).";

export function isValidBrokerDealerRegistrationNumber(value: string): boolean {
    const trimmed = (value ?? "").trim();
    if (!trimmed || trimmed.length > BROKER_DEALER_REGISTRATION_MAX_LENGTH) return false;
    return REGISTRATION_PATTERNS.some((pattern) => pattern.test(trimmed));
}

/**
 * The form a postcode is stored in: upper case with DEFRA's single space ("ls14ap" → "LS1 4AP", "d02x285" → "D02 X285").
 * Anything else comes back upper-cased with its spacing collapsed, for validation to refuse.
 */
export function normalizeBrokerDealerPostcode(value: string): string {
    const upper = (value ?? "").trim().toUpperCase().replace(/\s+/g, " ");
    if (!upper) return "";
    const compact = upper.replace(/ /g, "");
    if (compact.length > 3) {
        const uk = `${compact.slice(0, -3)} ${compact.slice(-3)}`;
        if (UK_POSTCODE.test(uk)) return uk;
    }
    if (compact.length === 7) {
        const eircode = `${compact.slice(0, 3)} ${compact.slice(3)}`;
        if (IRISH_EIRCODE.test(eircode)) return eircode;
    }
    return upper;
}

export function isValidBrokerDealerPostcode(value: string): boolean {
    const normalized = normalizeBrokerDealerPostcode(value);
    return (
        normalized !== "" &&
        normalized.length <= BROKER_DEALER_POSTCODE_MAX_LENGTH &&
        (UK_POSTCODE.test(normalized) || IRISH_EIRCODE.test(normalized))
    );
}

function utf8Length(value: string): number {
    return new TextEncoder().encode(value).length;
}

function isValidEmailDomain(domain: string): boolean {
    if (!domain || domain.length > 256) return false;
    if ([...domain].some((c) => c.charCodeAt(0) <= 0x20 || EMAIL_DOMAIN_SEPARATORS.includes(c))) return false;
    let ascii = domain;
    if ([...domain].some((c) => c.charCodeAt(0) > 0x7f)) {
        try {
            ascii = new URL(`http://${domain}`).hostname;
        } catch {
            return false;
        }
    }
    const labels = ascii.split(".");
    if (labels.length < 2) return false;
    return labels.every((label, index) => {
        if (!label || label.length > 63) return false;
        return (index < labels.length - 1 ? EMAIL_DOMAIN_LABEL : EMAIL_TOP_LEVEL_LABEL).test(label);
    });
}

export function isValidBrokerDealerEmailAddress(value: string): boolean {
    const email = (value ?? "").trim().normalize("NFC");
    if (!email || email.length > BROKER_DEALER_EMAIL_MAX_LENGTH) return false;
    const parts = email.split("@");
    if (parts.length !== 2) return false;
    const [local, domain] = parts;
    if (!local || utf8Length(local) > 64) return false;
    if (local.split(".").some((segment) => !EMAIL_LOCAL_SEGMENT.test(segment))) return false;
    return isValidEmailDomain(domain);
}

/** Whether the saved record has a broker/dealer (it is only ever removed deliberately). */
export function hasStoredBrokerDealer(detail: IWasteMovementDetail | null | undefined): boolean {
    return !!detail?.brokerDealer;
}

/**
 * Form values for the broker/dealer section. A new movement starts unanswered; a saved one answers Yes when it has a
 * broker/dealer and No otherwise (nothing is recorded or sent for No).
 */
export function brokerDealerFormValuesFromDetail(detail: IWasteMovementDetail | null | undefined): WasteMovementBrokerDealerFormValues {
    const broker = detail?.brokerDealer;
    return {
        brokerDealerInvolved: detail ? (broker ? "true" : "false") : "",
        brokerDealerOrganisationName: broker?.organisationName ?? "",
        brokerDealerRegistrationNumber: broker?.registrationNumber ?? "",
        brokerDealerFullAddress: broker?.fullAddress ?? "",
        brokerDealerPostcode: broker?.postcode ?? "",
        brokerDealerEmailAddress: broker?.emailAddress ?? "",
        brokerDealerPhoneNumber: broker?.phoneNumber ?? "",
        confirmRemoveBrokerDealer: false,
    };
}

/** Answering Yes counts as engaging receipt capture, like the backend's HasAnyDwtCapture with a broker/dealer. */
export function hasAnyBrokerDealerValue(values: Pick<WasteMovementFormValues, "brokerDealerInvolved">): boolean {
    return values.brokerDealerInvolved === "true";
}

export type BrokerDealerPayload = {
    brokerDealer?: WasteMovementBrokerDealerPayload;
    clearBrokerDealer?: boolean;
};

function textOrNull(value: string): string | null {
    const t = (value ?? "").trim();
    return t === "" ? null : t;
}

/** A saved broker/dealer is only removed after the operator answers No and confirms it (never by leaving it blank). */
export function needsBrokerDealerRemovalConfirmation(
    values: Pick<WasteMovementFormValues, "brokerDealerInvolved">,
    context: { isNew: boolean; hasStored: boolean }
): boolean {
    return !context.isNew && context.hasStored && values.brokerDealerInvolved === "false";
}

/**
 * S1D part of the write payload, following the API's explicit semantics: while the answer is Yes the six values are sent
 * as a whole (replacing what is stored); a saved broker/dealer is removed only through `clearBrokerDealer`, sent after the
 * operator answered No and confirmed; otherwise nothing is sent, so an unrelated edit never touches what is stored.
 */
export function buildBrokerDealerPayload(
    values: WasteMovementFormValues,
    movementDirection: WasteMovementDirection,
    args: { isNew: boolean; detail: IWasteMovementDetail | null | undefined }
): BrokerDealerPayload {
    if (movementDirection !== "Received") return {};
    if (values.brokerDealerInvolved === "true") {
        return {
            brokerDealer: {
                organisationName: textOrNull(values.brokerDealerOrganisationName),
                registrationNumber: textOrNull(values.brokerDealerRegistrationNumber),
                fullAddress: textOrNull(values.brokerDealerFullAddress),
                postcode: textOrNull(values.brokerDealerPostcode),
                emailAddress: textOrNull(values.brokerDealerEmailAddress),
                phoneNumber: textOrNull(values.brokerDealerPhoneNumber),
            },
        };
    }
    const removal = needsBrokerDealerRemovalConfirmation(values, { isNew: args.isNew, hasStored: hasStoredBrokerDealer(args.detail) });
    if (removal && values.confirmRemoveBrokerDealer) return { clearBrokerDealer: true };
    return {};
}

export type BrokerDealerErrors = Partial<Record<keyof WasteMovementBrokerDealerFormValues, string>>;

export type BrokerDealerValidationContext = {
    movementDirection: WasteMovementDirection;
    captureRequired: boolean;
    isNew: boolean;
    hasStored: boolean;
};

/** Client mirror of the server's broker/dealer rules so the operator sees them before saving; the server stays authoritative. */
export function validateBrokerDealer(values: WasteMovementFormValues, context: BrokerDealerValidationContext): BrokerDealerErrors {
    const errors: BrokerDealerErrors = {};
    if (context.movementDirection !== "Received" || !context.captureRequired) return errors;

    if (values.brokerDealerInvolved === "") {
        errors.brokerDealerInvolved = "Select whether a broker or dealer was involved.";
        return errors;
    }
    if (values.brokerDealerInvolved !== "true") {
        if (needsBrokerDealerRemovalConfirmation(values, context) && !values.confirmRemoveBrokerDealer)
            errors.confirmRemoveBrokerDealer = "Confirm that the recorded broker or dealer should be removed, or answer Yes.";
        return errors;
    }

    const name = values.brokerDealerOrganisationName.trim();
    if (!name) errors.brokerDealerOrganisationName = "Broker or dealer organisation name is required.";
    else if (name.length > BROKER_DEALER_NAME_MAX_LENGTH)
        errors.brokerDealerOrganisationName = `Broker or dealer organisation name must be at most ${BROKER_DEALER_NAME_MAX_LENGTH} characters.`;

    const registration = values.brokerDealerRegistrationNumber.trim();
    if (registration && !isValidBrokerDealerRegistrationNumber(registration))
        errors.brokerDealerRegistrationNumber = BROKER_DEALER_REGISTRATION_FORMAT_MESSAGE;

    const address = values.brokerDealerFullAddress.trim();
    const postcode = values.brokerDealerPostcode.trim();
    if (address.length > BROKER_DEALER_ADDRESS_MAX_LENGTH)
        errors.brokerDealerFullAddress = `Broker or dealer address must be at most ${BROKER_DEALER_ADDRESS_MAX_LENGTH} characters.`;
    if (address && !postcode) errors.brokerDealerPostcode = "Enter the broker or dealer's postcode with their address.";
    else if (postcode && !isValidBrokerDealerPostcode(postcode))
        errors.brokerDealerPostcode = "Broker or dealer postcode must be a valid UK postcode or Irish Eircode.";

    const email = values.brokerDealerEmailAddress.trim();
    if (email && !isValidBrokerDealerEmailAddress(email)) errors.brokerDealerEmailAddress = "Broker or dealer email address is not valid.";

    if (values.brokerDealerPhoneNumber.trim().length > BROKER_DEALER_PHONE_MAX_LENGTH)
        errors.brokerDealerPhoneNumber = `Broker or dealer phone number must be at most ${BROKER_DEALER_PHONE_MAX_LENGTH} characters.`;

    return errors;
}
